import React, { useState, useEffect } from "react";
import ScriptSelector from "./ScriptSelector";
import { API_BASE_URL, enhancedFetch } from "../services/api";

const ScriptExecution = ({ token, onUnauthorized, setError, setSuccess }) => {
  const [selectedScript, setSelectedScript] = useState("");
  const [scriptArgs, setScriptArgs] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setErrorFlg] = useState(null);
  const [mafCode, setMafCode] = useState("");
  const [showMafInput, setShowMafInput] = useState(false);
  const [scripts, setScripts] = useState([]);
  const [showResultModal, setShowResultModal] = useState(false); // 控制结果弹窗显示

  const handleError = (error) => {
    setError(error);
    setErrorFlg(error);
  };
  // 获取脚本列表
  useEffect(() => {
    const fetchScripts = async () => {
      try {
        const response = await enhancedFetch(
          `${API_BASE_URL}/scripts/list/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
          onUnauthorized
        );

        if (response.ok) {
          const data = await response.json();
          setScripts(data);
        }
      } catch (err) {
        handleError("获取脚本列表失败: " + err.message);
      }
    };

    if (token) {
      fetchScripts();
    }
  }, [token, onUnauthorized]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        handleError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // 生成参数模板
  const generateParamTemplate = () => {
    if (!selectedScript) return;

    const selected = scripts.find((script) =>
      typeof script === "string"
        ? script === selectedScript
        : script.name === selectedScript
    );

    if (selected && typeof selected !== "string" && selected.param_hints) {
      const template = {};
      Object.keys(selected.param_hints).forEach((paramName) => {
        // 移除参数前缀（如--）
        const cleanParamName = paramName.replace(/^--/, "");
        template[cleanParamName] = ""; // 默认空值
      });

      setScriptArgs(JSON.stringify(template, null, 2));
    }
  };

  // 执行脚本
  const executeScript = async (e) => {
    e.preventDefault();

    if (!selectedScript) {
      handleError("请选择要执行的脚本");
      return;
    }

    setLoading(true);
    handleError(null);
    setResult(null);

    try {
      // 解析参数为JSON对象
      let args = null;
      if (scriptArgs.trim()) {
        try {
          args = JSON.parse(scriptArgs);
        } catch (parseError) {
          throw new Error("参数JSON格式无效: " + parseError.message);
        }
      }

      // 准备请求头
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      // 如果需要MAF认证，添加MAF代码到请求头
      if (showMafInput && mafCode) {
        headers["X-Maf-Code"] = mafCode;
      }

      const response = await enhancedFetch(
        `${API_BASE_URL}/scripts/execute/`,
        {
          method: "POST",
          headers: headers,
          body: JSON.stringify({
            script_path: selectedScript,
            args: args,
          }),
        },
        onUnauthorized
      );

      const data = await response.json();

      if (response.ok) {
        setResult(data);
        setShowResultModal(true); // 显示结果弹窗
        // 执行成功后重置MAF相关状态
        setShowMafInput(false);
        setMafCode("");
      } else {
        // 检查是否需要MAF认证
        if (
          response.status === 401 &&
          response.headers.get("X-MAF-Required") === "true"
        ) {
          setShowMafInput(true);
          handleError("需要MAF认证。请输入您的MAF验证码。");
        } else {
          handleError(data.detail || "执行脚本失败");
        }
      }
    } catch (err) {
      handleError("网络错误: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 取消MAF输入
  const cancelMafInput = () => {
    setShowMafInput(false);
    setMafCode("");
    handleError(null);
  };

  // 关闭结果弹窗
  const closeResultModal = () => {
    setShowResultModal(false);
    setResult(null);
  };

  return (
    <>
      {/* 结果弹窗 */}
      {showResultModal && result && (
        <div className="modal-overlay" onClick={closeResultModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>执行结果</h3>
              <button className="modal-close" onClick={closeResultModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="result-message">{result.message}</p>
              <pre className="result-output">
                {JSON.stringify(result.output, null, 2)}
              </pre>
            </div>
            <div className="modal-footer">
              <button className="primary-button" onClick={closeResultModal}>
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="card">
        <div className="card-header">
          <h2>脚本执行</h2>
          <p>选择并执行脚本，可选参数</p>
        </div>

        <div>
          <form onSubmit={executeScript} className="script-form">
            <ScriptSelector
              scripts={scripts}
              selectedScript={selectedScript}
              onScriptSelect={setSelectedScript}
              token={token}
            />

            <div className="form-group">
              <div className="args-header">
                <label htmlFor="scriptArgs">脚本参数 (JSON格式):</label>
                <button
                  type="button"
                  onClick={generateParamTemplate}
                  disabled={!selectedScript}
                  className="secondary-button small-button"
                >
                  生成模板
                </button>
              </div>
              <textarea
                id="scriptArgs"
                value={scriptArgs}
                onChange={(e) => setScriptArgs(e.target.value)}
                placeholder='{"key": "value", "number": 123}'
                className="args-textarea"
              />
              <div className="help-text">
                以JSON对象形式输入参数。如果不需要参数，请留空。
              </div>
            </div>

            {showMafInput && (
              <div className="form-group maf-input-group">
                <label htmlFor="mafCode">MAF验证码:</label>
                <input
                  type="text"
                  id="mafCode"
                  value={mafCode}
                  onChange={(e) => setMafCode(e.target.value)}
                  placeholder="输入6位验证码"
                  maxLength="6"
                  className="maf-input"
                />
                <div className="help-text">
                  请输入您身份验证器应用中的验证码
                </div>
              </div>
            )}

            <div className="form-actions">
              <button
                type="submit"
                disabled={loading || (showMafInput && mafCode.length !== 6)}
                className="primary-button"
              >
                {loading ? (
                  <span>
                    <span className="spinner"></span> 执行中...
                  </span>
                ) : (
                  "执行脚本"
                )}
              </button>

              {showMafInput && (
                <button
                  type="button"
                  onClick={cancelMafInput}
                  className="secondary-button"
                  disabled={loading}
                >
                  取消
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ScriptExecution;
